// src/components/tables/BusinessTable.js
import React from 'react';
import { FileText, ClipboardList, ChevronLeft, ChevronRight } from 'lucide-react';

const BusinessTable = ({ 
  businesses, 
  filteredBusinesses, 
  currentItems, 
  clients, 
  itemsPerPage, 
  currentPage, 
  totalPages, 
  onLicenseClick,
  onLogClick,
  onPageChange,
  startIndex,
  endIndex,
  searchTerm
}) => {
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden h-full flex flex-col">
      {/* TABLE WITH SCROLL */}
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Business ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Business Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Industry Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Client ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {currentItems.length > 0 ? (
              currentItems.map((business, index) => {
                const client = clients.find(
                  c => c.client_id === business.client_id
                );

                return (
                  <tr
                    key={business.business_id || index}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {business.business_id || 'Not available'}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {business.business_name || 'Unnamed'}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.industry_type || 'Not specified'}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {business.client_id || 'Not specified'}
                        </span>
                        {client && (
                          <span className="text-xs text-gray-500">
                            {client.client_name}
                            {client.client_email
                              ? ` (${client.client_email})`
                              : ''}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-3">
                        <button
                          onClick={() => onLicenseClick(business)}
                          className="text-blue-600 hover:text-blue-900 font-medium flex items-center space-x-1 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded transition-colors"
                          title="View Licenses"
                        >
                          <FileText size={16} />
                          <span>License</span>
                        </button>

                        <button
                          onClick={() => onLogClick(business)}
                          className="text-green-600 hover:text-green-900 font-medium flex items-center space-x-1 bg-green-50 hover:bg-green-100 px-3 py-1 rounded transition-colors"
                          title="View Logs"
                        >
                          <ClipboardList size={16} />
                          <span>Log</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan="5"
                  className="px-6 py-8 text-center text-gray-500"
                >
                  {businesses.length === 0
                    ? 'No businesses found'
                    : 'No businesses match your search'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {filteredBusinesses.length > itemsPerPage && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 border rounded-md ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 border rounded-md ${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>

          <div className="hidden sm:flex sm:items-center sm:justify-between w-full">
            <p className="text-sm text-gray-700">
              Showing{' '}
              <span className="font-semibold">{startIndex + 1}</span> to{' '}
              <span className="font-semibold">
                {Math.min(endIndex, filteredBusinesses.length)}
              </span>{' '}
              of{' '}
              <span className="font-semibold">
                {filteredBusinesses.length}
              </span>{' '}
              businesses {searchTerm && '(filtered)'}
            </p>

            <nav className="inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-2 py-2 border rounded-l-md"
              >
                <ChevronLeft size={18} />
              </button>

              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`px-4 py-2 border ${
                      page === currentPage
                        ? 'bg-blue-50 text-blue-600'
                        : 'bg-white text-gray-500'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}

              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-2 py-2 border rounded-r-md"
              >
                <ChevronRight size={18} />
              </button>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessTable;